/**
 * StatusBar Component
 *
 * Displays session statistics, context usage, and permission mode.
 * Ported from V1 with Tailwind gray-* classes for V2.
 *
 * @module components/session/StatusBar
 */

import { useState, memo } from 'react';
import {
  Coins, MessageSquare, Database, Zap, Loader2,
  Brain, ShieldCheck, FileEdit, ChevronDown, AlertTriangle,
  StickyNote, Code, Sparkles, Cpu
} from 'lucide-react';

// Whisper mode definitions
const WHISPER_MODES = [
  { value: 'enabled', label: 'Enabled', color: 'text-amber-400', description: 'Whispers active (default)' },
  { value: 'disabled', label: 'Disabled', color: 'text-gray-500', description: 'No whispers for this agent' },
  { value: 'focus', label: 'Focus', color: 'text-blue-400', description: 'Paused until session ends' },
];

// Permission mode definitions with display info
const PERMISSION_MODES = [
  { value: 'default', label: 'Default', icon: ShieldCheck, color: 'text-blue-400', description: 'Prompts for dangerous tools' },
  { value: 'acceptEdits', label: 'Accept Edits', icon: FileEdit, color: 'text-green-400', description: 'Auto-accept file edits' },
  { value: 'plan', label: 'Plan', icon: Brain, color: 'text-purple-400', description: 'Planning mode only' },
  { value: 'bypassPermissions', label: 'Bypass', icon: Zap, color: 'text-orange-400', description: 'Allow all tools (careful!)' },
];

// Model tier definitions with display info
const MODEL_TIERS = [
  { value: 'opus', label: 'Opus', icon: Sparkles, color: 'text-purple-400', description: 'Most capable, complex tasks' },
  { value: 'sonnet', label: 'Sonnet', icon: Cpu, color: 'text-blue-400', description: 'Balanced speed and quality' },
  { value: 'haiku', label: 'Haiku', icon: Zap, color: 'text-green-400', description: 'Fast, lightweight tasks' },
];

/**
 * Get model tier info from model ID or alias
 */
function getModelTier(model) {
  if (!model) return null;
  const lower = model.toLowerCase();
  if (lower.includes('opus')) return MODEL_TIERS.find(t => t.value === 'opus');
  if (lower.includes('haiku')) return MODEL_TIERS.find(t => t.value === 'haiku');
  if (lower.includes('sonnet')) return MODEL_TIERS.find(t => t.value === 'sonnet');
  return null;
}

function StatusBar({
  sessionStats,
  isProcessing,
  connected,
  sessionStatus,
  permissionMode = 'default',
  controlInitialized,
  onChangeMode,
  currentModel = null,
  onChangeModel,
  selfNoteEnabled = false,
  selfNoteThreshold = 74,
  onToggleSelfNote,
  onChangeSelfNoteThreshold,
  rawModeEnabled = false,
  onToggleRawMode,
  whisperMode = 'enabled',
  onChangeWhisperMode,
}) {
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [showThresholdSlider, setShowThresholdSlider] = useState(false);
  const [showWhisperMenu, setShowWhisperMenu] = useState(false);

  const {
    totalCost = 0,
    inputTokens = 0,
    outputTokens = 0,
    cacheReadTokens = 0,
    cacheCreationTokens = 0,
    numTurns = 0,
    contextWindow = 200000,
    contextLeftPercent = null,
    tokensUsed = 0,
    isCompacting = false,
  } = sessionStats || {};

  // Get current mode info
  const currentMode = PERMISSION_MODES.find(m => m.value === permissionMode) || PERMISSION_MODES[0];
  const ModeIcon = currentMode.icon;

  // Context used percentage (0-100) - bar fills up as context is consumed
  const contextRemaining = contextLeftPercent;
  const contextUsedPercent = contextRemaining !== null ? (100 - contextRemaining) : 0;

  // Compact threshold - auto-compact triggers at 78%
  const COMPACT_THRESHOLD = 78;
  const WARNING_THRESHOLD = 77;
  const ORANGE_THRESHOLD = 70;

  // Determine color based on usage (higher usage = more warning)
  const getContextColor = (usedPercent) => {
    if (usedPercent === null) return 'bg-gray-600';
    if (usedPercent >= 95) return 'bg-red-500';
    if (usedPercent >= 85) return 'bg-red-400';
    if (usedPercent >= ORANGE_THRESHOLD) return 'bg-orange-400';
    if (usedPercent >= 50) return 'bg-yellow-300';
    return 'bg-green-400';
  };

  const getContextTextColor = (usedPercent) => {
    if (usedPercent === null) return 'text-gray-500';
    if (usedPercent >= 95) return 'text-red-400 font-medium animate-pulse';
    if (usedPercent >= 85) return 'text-red-400';
    if (usedPercent >= ORANGE_THRESHOLD) return 'text-orange-400';
    return 'text-green-400';
  };

  // Show warning icon when approaching compact threshold
  const showCompactWarning = contextUsedPercent >= WARNING_THRESHOLD;

  // Format cost
  const formatCost = (cost) => {
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    if (cost < 1) return `$${cost.toFixed(3)}`;
    return `$${cost.toFixed(2)}`;
  };

  // Format token count
  const formatTokens = (tokens) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
    return tokens.toString();
  };

  return (
    <div className="bg-gray-900 border-t border-gray-700 px-4 py-2">
      <div className="flex items-center justify-between text-xs">
        {/* Left side: Stats */}
        <div className="flex items-center gap-4">
          {/* Connection status */}
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-gray-400">{connected ? 'Connected' : 'Disconnected'}</span>
          </div>

          {/* Processing indicator */}
          {isProcessing && (
            <div className="flex items-center gap-1.5 text-orange-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Processing...</span>
            </div>
          )}

          {/* Paused/Reconnecting indicator */}
          {sessionStatus === 'paused' && (
            <div className="flex items-center gap-1.5 text-yellow-500 animate-pulse">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>⟳ Reconnecting...</span>
            </div>
          )}

          {/* Compacting indicator */}
          {isCompacting && (
            <div className="flex items-center gap-1.5 text-purple-400">
              <Zap className="w-3 h-3 animate-pulse" />
              <span>Compacting context...</span>
            </div>
          )}

          {/* Turns */}
          {numTurns > 0 && (
            <div className="flex items-center gap-1.5 text-gray-400">
              <MessageSquare className="w-3 h-3" />
              <span>{numTurns} turns</span>
            </div>
          )}

          {/* Cost */}
          {totalCost > 0 && (
            <div className="flex items-center gap-1.5 text-gray-400">
              <Coins className="w-3 h-3" />
              <span>{formatCost(totalCost)}</span>
            </div>
          )}

          {/* Permission Mode Toggle */}
          {onChangeMode && (
            <div className="relative">
              <button
                onClick={() => setShowModeMenu(!showModeMenu)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded ${currentMode.color} hover:bg-gray-800 transition-colors`}
                title={currentMode.description}
              >
                <ModeIcon className="w-3 h-3" />
                <span>{currentMode.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showModeMenu ? 'rotate-180' : ''}`} />
              </button>

              {showModeMenu && (
                <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-600 rounded shadow-lg py-1 min-w-[160px] z-50">
                  {PERMISSION_MODES.map((mode) => {
                    const Icon = mode.icon;
                    const isActive = mode.value === permissionMode;
                    return (
                      <button
                        key={mode.value}
                        onClick={() => {
                          onChangeMode(mode.value);
                          setShowModeMenu(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-700 transition-colors ${
                          isActive ? mode.color : 'text-gray-300'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <div>
                          <div className="text-xs font-medium">{mode.label}</div>
                          <div className="text-[10px] text-gray-500">{mode.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Model Switcher */}
          {onChangeModel && (
            <div className="relative">
              <button
                onClick={() => setShowModelMenu(!showModelMenu)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-gray-800 transition-colors ${
                  getModelTier(currentModel)?.color || 'text-gray-400'
                }`}
                title={currentModel ? `Model: ${currentModel}` : 'Select model'}
              >
                {(() => {
                  const tier = getModelTier(currentModel);
                  const Icon = tier?.icon || Cpu;
                  return <Icon className="w-3 h-3" />;
                })()}
                <span>{getModelTier(currentModel)?.label || currentModel || 'Model'}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showModelMenu ? 'rotate-180' : ''}`} />
              </button>

              {showModelMenu && (
                <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-600 rounded shadow-lg py-1 min-w-[160px] z-50">
                  {MODEL_TIERS.map((tier) => {
                    const Icon = tier.icon;
                    const isActive = getModelTier(currentModel)?.value === tier.value;
                    return (
                      <button
                        key={tier.value}
                        onClick={() => {
                          onChangeModel(tier.value);
                          setShowModelMenu(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-700 transition-colors ${
                          isActive ? tier.color : 'text-gray-300'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <div>
                          <div className="text-xs font-medium">{tier.label}</div>
                          <div className="text-[10px] text-gray-500">{tier.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Self-Note Toggle */}
          {onToggleSelfNote && (
            <div className="relative">
              <button
                onClick={() => onToggleSelfNote(!selfNoteEnabled)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded transition-colors ${
                  selfNoteEnabled ? 'text-amber-400 hover:bg-gray-800' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
                }`}
                title={selfNoteEnabled ? 'Self-Note enabled - click to disable' : 'Self-Note disabled - click to enable'}
              >
                <StickyNote className="w-3 h-3" />
                <span>{selfNoteEnabled ? 'ON' : 'OFF'}</span>
              </button>
            </div>
          )}

          {/* Self-Note Threshold */}
          {selfNoteEnabled && onChangeSelfNoteThreshold && (
            <div className="relative">
              <button
                onClick={() => setShowThresholdSlider(!showThresholdSlider)}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-amber-400/70 hover:text-amber-400 hover:bg-gray-800 transition-colors"
                title={`Self-Note triggers at ${selfNoteThreshold}% context usage`}
              >
                <span className="text-[10px]">{selfNoteThreshold}%</span>
                <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showThresholdSlider ? 'rotate-180' : ''}`} />
              </button>

              {showThresholdSlider && (
                <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-600 rounded shadow-lg p-3 min-w-[180px] z-50">
                  <div className="text-xs text-gray-400 mb-2">Self-Note Threshold</div>
                  <input
                    type="range"
                    min="50"
                    max="90"
                    value={selfNoteThreshold}
                    onChange={(e) => onChangeSelfNoteThreshold(parseInt(e.target.value, 10))}
                    className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                    <span>50%</span>
                    <span className="text-amber-400 font-medium">{selfNoteThreshold}%</span>
                    <span>90%</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Raw Mode Toggle - Debug view showing JSON */}
          {onToggleRawMode && (
            <button
              onClick={onToggleRawMode}
              className={`flex items-center gap-1.5 px-2 py-0.5 rounded transition-colors ${
                rawModeEnabled
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
              }`}
              title={rawModeEnabled ? 'Raw mode ON - showing JSON structure' : 'Raw mode OFF - click to view message JSON'}
            >
              <Code className="w-3 h-3" />
              <span>{rawModeEnabled ? 'RAW' : 'VIEW'}</span>
            </button>
          )}

          {/* Whisper Mode Toggle */}
          {onChangeWhisperMode && (
            <div className="relative">
              <button
                onClick={() => setShowWhisperMenu(!showWhisperMenu)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded transition-colors ${
                  whisperMode === 'enabled'
                    ? 'text-amber-400 hover:bg-gray-800'
                    : whisperMode === 'focus'
                    ? 'text-blue-400 hover:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
                }`}
                title={`Whisper mode: ${whisperMode}`}
              >
                <Sparkles className="w-3 h-3" />
                <span>{whisperMode === 'enabled' ? 'ON' : whisperMode === 'focus' ? 'FOCUS' : 'OFF'}</span>
                <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showWhisperMenu ? 'rotate-180' : ''}`} />
              </button>

              {showWhisperMenu && (
                <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-600 rounded shadow-lg py-1 min-w-[140px] z-50">
                  {WHISPER_MODES.map((mode) => {
                    const isActive = mode.value === whisperMode;
                    return (
                      <button
                        key={mode.value}
                        onClick={() => {
                          onChangeWhisperMode(mode.value);
                          setShowWhisperMenu(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-700 transition-colors ${
                          isActive ? mode.color : 'text-gray-300'
                        }`}
                      >
                        <div>
                          <div className="text-xs font-medium">{mode.label}</div>
                          <div className="text-[10px] text-gray-500">{mode.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side: Context usage with progress bar */}
        <div className="flex items-center gap-3">
          {/* Context progress bar - always visible when we have data */}
          {contextRemaining !== null && (
            <div className="flex items-center gap-2">
              <Database className="w-3 h-3 text-gray-500" />
              <div className="flex items-center gap-2">
                {/* Progress bar container - fills up as context is used */}
                <div
                  className="relative w-24 h-2 bg-gray-700 rounded-full overflow-hidden"
                  title={`${tokensUsed.toLocaleString()} / ${contextWindow.toLocaleString()} tokens used\nAuto-compact at ${COMPACT_THRESHOLD}%`}
                >
                  {/* Progress fill */}
                  <div
                    className={`h-full transition-all duration-300 ${getContextColor(contextUsedPercent)}`}
                    style={{ width: `${contextUsedPercent}%` }}
                  />
                  {/* Compact threshold marker at 78% */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-red-500/70"
                    style={{ left: `${COMPACT_THRESHOLD}%` }}
                    title={`Auto-compact at ${COMPACT_THRESHOLD}%`}
                  />
                </div>
                {/* Warning icon when approaching compact threshold */}
                {showCompactWarning && (
                  <AlertTriangle
                    className="w-3 h-3 text-orange-400 animate-pulse"
                    title="Approaching auto-compact threshold"
                  />
                )}
                {/* Percentage text - shows how full the context is */}
                <span className={`min-w-[45px] text-right ${getContextTextColor(contextUsedPercent)}`}>
                  {contextUsedPercent}%
                </span>
              </div>
            </div>
          )}

          {/* Show "waiting" state if no context data yet */}
          {contextRemaining === null && connected && (
            <div className="flex items-center gap-2 text-gray-500">
              <Database className="w-3 h-3" />
              <span>Context: --</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(StatusBar);
